'use client';

import React from 'react';
import { useLeadGenerationInteraction } from 'components/analytics/hooks/useLeadGenerationInteraction';
import { LEAD_GENERATION_INTERACTION_TYPES } from 'components/analytics/types';
import { getCookieValue, convertObjectToArray } from 'components/analytics/utils';
import { NINETAILED_ANONYMOUS_ID_COOKIE } from 'utils/cookies';
import {
  AnalyticsModuleTrackingProvider,
  useAnalyticsModuleTracking,
} from 'components/analytics/AnalyticsModuleTracking';
import type { LeadGenerationInteractionProps } from 'components/analytics/types';
import { ExperienceType } from '@ninetailed/experience.js-shared';
import { getVariantDisplayName } from './getVariantDisplayName';

interface NinetailedExperienceTrackerProps {
  experienceName: string;
  experienceSnowflakeName: string | null;
  experienceId: string;
  variantName: string;
  variantId: string;
  variantIndex: number;
  experienceType?: ExperienceType | undefined;
}

const NinetailedExperienceTrackerInner: React.FC<NinetailedExperienceTrackerProps> = ({
  experienceName,
  experienceSnowflakeName,
  experienceId,
  variantName,
  variantId,
  variantIndex,
  experienceType,
}) => {
  const hasTracked = React.useRef(false);
  const { attributes } = useAnalyticsModuleTracking();

  const experienceData = React.useMemo(
    () => ({
      ninetailed_id: getCookieValue(NINETAILED_ANONYMOUS_ID_COOKIE) ?? '', // Ninetailed visitor anonymous ID
      experience_name: experienceSnowflakeName || experienceName, // Name for Ninetailed Experience
      experience_id: experienceId, // Entry ID for Ninetailed Experience
      variant_name: getVariantDisplayName(variantIndex, variantName, experienceType), // Internal Name or standardized variant name
      variant_id: variantId, // Entry ID for variant entry
      experience_type: experienceType, // Type of experience
    }),
    [experienceName, experienceSnowflakeName, experienceId, variantName, variantId, variantIndex, experienceType],
  );

  const analyticsProps: LeadGenerationInteractionProps = {
    interaction_type: LEAD_GENERATION_INTERACTION_TYPES.NINETAILED_EXPERIENCE_VIEWED,
    interaction_details: {
      location: attributes.moduleLocation,
      action_detail: 'experience_viewed',
    },
    additional_data: convertObjectToArray(experienceData),
  };

  const trackEvent = useLeadGenerationInteraction(analyticsProps);

  // Only fire the event when the user is bucketed into an experience (Control, Variant N, or personalization).
  // When experienceType is undefined we're showing baseline to someone not in the experiment and shouldn't track.
  const shouldTrack = experienceType !== undefined;

  // trackNinetailedExperienceViewed is now fired by NinetailedInlineTracker (an inline <script>)
  // so it survives React hydration failures in Meta in-app browsers.
  // This useEffect only handles the Segment/Ninetailed lead generation interaction event.
  React.useEffect(() => {
    if (shouldTrack && !hasTracked.current) {
      trackEvent();
      hasTracked.current = true;
    }
  }, [trackEvent, shouldTrack]);

  return null;
};

export const NinetailedExperienceTracker: React.FC<NinetailedExperienceTrackerProps> = props => {
  return (
    <AnalyticsModuleTrackingProvider>
      <NinetailedExperienceTrackerInner {...props} />
    </AnalyticsModuleTrackingProvider>
  );
};
